// Thanh công cụ trên cây mạng lưới: ô tìm ("N kết quả" ngay trong ô khi đang tìm — máy tính), Tất cả / Cần xử lý.
//  · Máy tính (sơ đồ theo nhóm): vạch đường đang xem (bấm một chặng là về đó) + Quay lại (bước trước; đang tìm / lọc
//    thì thoát ra) + Đóng hết (về lúc mới mở trang). Không có "Mở hết" — sơ đồ mở từng nhánh.
//  · Điện thoại: một nút đổi qua lại Mở hết / Thu gọn của cây thụt lề; đang lọc thì cây tự mở theo kết quả nên nút tắt.

import { ArrowLeft, ChevronsLeft } from 'lucide-react'
import { NETWORK_TYPE_LABEL, type NetworkUnitBase } from '@shoptrack/common/network-tree'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Segmented } from '@/components/ui/segmented'
import { cn } from '@/lib/utils'
import type { NetworkFilter } from './network-view'
import type { NetworkDrillView } from './useNetworkTreeState'

export function NetworkToolbar<U extends NetworkUnitBase & { orders30d?: number | null }>({
  compact,
  text,
  onText,
  placeholder,
  hits,
  filter,
  onFilter,
  issueCount,
  filtering,
  disabled,
  allOpen,
  onExpandAll,
  onCollapseAll,
  drill,
  rootName,
}: {
  compact: boolean
  text: string
  onText: (v: string) => void
  placeholder: string
  /** Số đơn vị khớp khi đang tìm. */
  hits: number
  filter: NetworkFilter
  onFilter: (f: NetworkFilter) => void
  issueCount: number
  filtering: boolean
  /** Cây rỗng. */
  disabled: boolean
  allOpen: boolean
  onExpandAll: () => void
  onCollapseAll: () => void
  /** Máy tính: đường đang xem + Quay lại / Đóng hết của sơ đồ theo nhóm. */
  drill: NetworkDrillView<U>
  /** Tên ô gốc — chặng đầu của vạch đường đi. */
  rootName: string
}) {
  const showHits = !compact && !!text.trim()
  return (
    <div className={cn('flex gap-2 border-b border-rule p-3', compact ? 'flex-col' : 'flex-wrap items-center')}>
      <div className={cn('relative min-w-0', !compact && 'flex-[0_1_320px] min-w-[200px]')}>
        <SearchInput
          value={text}
          onChange={(e) => onText(e.target.value)}
          placeholder={placeholder}
          aria-label="Tìm đơn vị"
          className={cn('desktop:h-9', showHits && 'pr-24')}
        />
        {showHits && (
          <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted" aria-live="polite">
            {hits.toLocaleString('vi-VN')} kết quả
          </span>
        )}
      </div>
      <div className={cn('flex items-center gap-2', !compact && 'contents')}>
        {/* Đang lọc theo loại (ô trên dải thống kê) thì cả hai nút đều không sáng */}
        <Segmented<NetworkFilter>
          value={filter}
          onChange={onFilter}
          label="Lọc đơn vị"
          options={[['all', 'Tất cả'], ['issues', `Cần xử lý${issueCount ? ` (${issueCount.toLocaleString('vi-VN')})` : ''}`]]}
        />
        {compact ? (
          <>
            <span className="flex-1" />
            <Button variant="outline" size="sm" onClick={allOpen ? onCollapseAll : onExpandAll} disabled={disabled || filtering}>
              {allOpen ? 'Thu gọn' : 'Mở hết'}
            </Button>
          </>
        ) : (
          <DrillNav drill={drill} rootName={rootName} disabled={disabled} />
        )}
      </div>
    </div>
  )
}

/** Vạch đường đang xem (Sunrise › Đại lý › Minh Phát › CTV) + Quay lại + Đóng hết. */
function DrillNav<U extends NetworkUnitBase & { orders30d?: number | null }>({ drill, rootName, disabled }: { drill: NetworkDrillView<U>; rootName: string; disabled: boolean }) {
  // Chặng = ô đang mở của từng cột (cột 1 trở đi); chặng thứ i ứng với ô thứ i trên đường mở
  const steps: Array<{ label: string; go: () => void }> = [{ label: rootName, go: () => drill.crumb(-1) }]
  drill.columns.slice(1).forEach((c, i) => {
    const it = c.open
    if (!it) return
    const label = it.kind === 'group' ? NETWORK_TYPE_LABEL[it.type] : it.kind === 'results' ? drill.resultsHead.label : it.kind === 'unit' ? it.node.unit.name : ''
    if (label) steps.push({ label, go: () => drill.crumb(i) })
  })
  return (
    <>
      <nav aria-label="Đang xem" className="flex min-w-0 flex-[1_1_200px] items-center overflow-hidden text-[12.5px]">
        {steps.map((s, i) => {
          const cur = i === steps.length - 1
          return (
            <span key={i} className={cn('flex min-w-0 items-center', cur ? 'shrink-0' : 'shrink')}>
              {i > 0 && <span aria-hidden className="shrink-0 px-0.5 text-muted/70">›</span>}
              {cur ? (
                <span aria-current="location" title={s.label} className="max-w-[220px] truncate px-[7px] py-[5px] font-semibold text-ink">
                  {s.label}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={s.go}
                  title={s.label}
                  className="motion-state max-w-[180px] truncate rounded-[7px] px-[7px] py-[5px] font-medium text-inkSoft hover:bg-paperSoft hover:text-ink"
                >
                  {s.label}
                </button>
              )}
            </span>
          )
        })}
      </nav>
      <Button variant="outline" size="sm" onClick={drill.back} disabled={disabled || !drill.canBack}>
        <ArrowLeft className="size-4" aria-hidden />
        Quay lại
      </Button>
      <Button variant="outline" size="sm" onClick={drill.closeAll} disabled={disabled || !drill.canCloseAll}>
        <ChevronsLeft className="size-4" aria-hidden />
        Đóng hết
      </Button>
    </>
  )
}
